import React, { ComponentPropsWithRef, useState } from 'react'
import { useHistory } from 'react-router'

import {
  useBreakpoint,
  Section,
  InfiniteList,
  Separator,
} from '@sketch/components'

import { useSignInToUnauthorizedWorkspace } from 'cloud-frontend'

import {
  PendingWorkspaceListItem,
  WorkspaceListItem,
} from './Item/WorkspaceListItem'

import { StyledTable } from './WorkspaceList.styles'

import { UserWorkspaceMembershipFragment } from '@sketch/gql-types'
import { useUserAuthorizations } from '@sketch/modules-common'
import { WorkspaceMembershipsFilter } from '../WorkspaceMembershipsFilter'

type WorkspaceFilterType = 'all' | 'sso' | 'pending'

const DESKTOP_TABLE_HEADER = [
  { label: 'Workspace Name' },
  { label: 'Role' },
  { label: 'Leave Workspace' },
]
const MOBILE_TABLE_HEADER = [
  { label: 'Workspace Name' },
  { label: 'Leave Workspace' },
]

interface WorkspaceListProps {
  memberships: UserWorkspaceMembershipFragment[]
  ssoMemberships: UserWorkspaceMembershipFragment[]
  pendingMemberships: UserWorkspaceMembershipFragment[]
  onLoadMore: ComponentPropsWithRef<typeof InfiniteList>['onLoadMore']
  linkButton?: React.ReactNode
  onLeaveWorkspace: (membership: UserWorkspaceMembershipFragment) => void
}

const WorkspaceList: React.FCC<WorkspaceListProps> = ({
  memberships,
  ssoMemberships,
  pendingMemberships,
  linkButton,
  onLoadMore,
  onLeaveWorkspace,
}) => {
  const history = useHistory()
  const isMobileAndSmaller = !useBreakpoint('xs')

  const { hasAccessToWorkspace } = useUserAuthorizations()
  const { signIn } = useSignInToUnauthorizedWorkspace()

  const handleWorkspaceClick = (
    event: React.MouseEvent<HTMLElement>,
    workspace: UserWorkspaceMembershipFragment['workspace']
  ) => {
    if (!hasAccessToWorkspace(workspace)) {
      event.preventDefault()

      const selectedWorkspace = memberships.find(
        membership => membership.workspace.identifier === workspace.identifier
      )

      if (selectedWorkspace) {
        const redirectURL = event.currentTarget.getAttribute('href')
        signIn({ workspace, onCompleted: () => history.push(redirectURL!) })
      }
    }
  }

  const tableHeaders = isMobileAndSmaller
    ? MOBILE_TABLE_HEADER
    : DESKTOP_TABLE_HEADER

  // Filters
  const [activeFilter, setActiveFilter] = useState<WorkspaceFilterType>('all')

  const handleFilterChange = (value: WorkspaceFilterType) => {
    setActiveFilter(value)
  }

  const listingByFilter = {
    sso: ssoMemberships,
    all: memberships,
    pending: pendingMemberships,
  }

  const filteredMemberships = listingByFilter[activeFilter]
  const isAllTabActive = activeFilter === 'all'

  return (
    <Section
      title=""
      text="Workspaces are where you’ll save your work and collaborate with others."
      action={linkButton}
    >
      <WorkspaceMembershipsFilter
        activeFilter={activeFilter}
        totalMemberships={memberships.length}
        totalSSOMemberships={ssoMemberships.length}
        totalPendingMemberships={pendingMemberships.length}
        onChange={handleFilterChange}
      />

      {pendingMemberships.length > 0 && isAllTabActive && (
        <div>
          <StyledTable
            header={tableHeaders}
            hideHeader
            items={pendingMemberships}
            data-testid="workspace-list"
            renderItem={(membership: UserWorkspaceMembershipFragment) => {
              return <PendingWorkspaceListItem membership={membership} />
            }}
          />
          {memberships.length - pendingMemberships.length > 0 && (
            <Separator marginX={3} marginY={1} />
          )}
        </div>
      )}

      <InfiniteList onLoadMore={onLoadMore}>
        <StyledTable
          header={tableHeaders}
          hideHeader
          items={filteredMemberships}
          data-testid="workspace-list"
          renderItem={(membership: UserWorkspaceMembershipFragment) => {
            if (!membership.acceptedAt) {
              return isAllTabActive ? null : (
                <PendingWorkspaceListItem membership={membership} />
              )
            }

            return (
              <WorkspaceListItem
                membership={membership}
                onLeaveWorkspace={onLeaveWorkspace}
                hasAccessToWorkspace={
                  hasAccessToWorkspace(membership.workspace) === true
                }
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  handleWorkspaceClick(event, membership.workspace)
                }
              />
            )
          }}
        />
      </InfiniteList>
    </Section>
  )
}

export default WorkspaceList
